import type { ReactNode } from "react";
import { CheckCircle2, CircleAlert, Info, TriangleAlert, type LucideIcon } from "lucide-react";

type Kind = "info" | "success" | "warning" | "danger";

/* A tinted panel with a solid rail on its leading edge: the rail carries the
   kind at a glance, the tint stays light enough for body text to read. */
const KIND: Record<Kind, string> = {
  info: "border-info/25 bg-info/[0.06] before:bg-info",
  success: "border-success/25 bg-success/[0.06] before:bg-success",
  warning: "border-warning/30 bg-warning/[0.07] before:bg-warning",
  danger: "border-danger/25 bg-danger/[0.06] before:bg-danger",
};

const ICON: Record<Kind, LucideIcon> = {
  info: Info,
  success: CheckCircle2,
  warning: TriangleAlert,
  danger: CircleAlert,
};

const ICON_TONE: Record<Kind, string> = {
  info: "text-info",
  success: "text-success",
  warning: "text-warning",
  danger: "text-danger",
};

export function Alert({
  kind = "info",
  title,
  children,
}: {
  kind?: Kind;
  title?: string;
  children?: ReactNode;
}) {
  const Icon = ICON[kind];
  return (
    <div
      className={`relative flex gap-3 overflow-hidden rounded-md border py-3 pr-4 pl-4 text-ink before:absolute before:inset-y-0 before:left-0 before:w-[3px] before:content-[''] ${KIND[kind]}`}
      role={kind === "danger" ? "alert" : "status"}
    >
      <Icon aria-hidden="true" className={`mt-px size-[1.125rem] shrink-0 ${ICON_TONE[kind]}`} strokeWidth={2.25} />
      <div className="min-w-0 text-sm leading-relaxed">
        {title ? <p className="font-title font-semibold text-ink">{title}</p> : null}
        {children ? <div className={title ? "mt-0.5 text-ink/85" : ""}>{children}</div> : null}
      </div>
    </div>
  );
}
